import { Box, Chip, IconButton, Paper, TableCell, TableRow, Tooltip, Typography } from '@mui/material'
import type { PrizeGroup } from 'types/Prize'
import { renderCells } from 'utils/renderCells'

import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import CheckIcon from '@mui/icons-material/Check'
import Link from '@mui/material/Link'
import { axiosDelete } from 'connectors/axiosDelete'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAbility } from '@casl/react'
import type { PureAbility } from '@casl/ability'
import type { AbilityType } from 'config/ability'
import { AbilityContext } from 'config/ability'
import { useAuthContext } from 'context/AuthContext'
import { useEffect, useState } from 'react'
import { useAlert } from 'context/AlertContext'
import { useTranslation } from 'react-i18next'

import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import { TrashIcon } from 'assets/icons/trash'
import { PenIcon } from 'assets/icons/pen'
import { LabelBox } from 'components/label-box'
import { format } from 'date-fns'
import PageLoader from 'components/PageLoader'
import { renderDate } from 'utils/renderDate'

const percent = (
  left: number,
  total: number
): {
  percent: string
  color: 'error' | 'warning' | 'success'
  icon: JSX.Element
} => {
  const value = Math.floor((left / total) * 100)
  if (value <= 10) {
    return {
      percent: `${value} %`,
      color: 'error',
      icon: <CloseOutlinedIcon />,
    }
  } else if (value <= 30) {
    return {
      percent: `${value} %`,
      color: 'warning',
      icon: <PriorityHighIcon />,
    }
  } else if (value <= 50) {
    return {
      percent: `${value} %`,
      color: 'warning',
      icon: <PriorityHighIcon />,
    }
  }
  return {
    percent: `${value} %`,
    color: 'success',
    icon: <CheckIcon />,
  }
}

export const PrizeGroupRow = ({ idx, queryKey, ...row }: PrizeGroup & { idx: number; queryKey: string }) => {
  const [show, setShow] = useState(false)
  const ability = useAbility<PureAbility<AbilityType>>(AbilityContext)
  const { token, refreshToken } = useAuthContext()
  const queryClient = useQueryClient()
  const { changeMessage } = useAlert()
  const { t } = useTranslation()
  // const theme = useTheme()

  const toggleShow = () => {
    setShow(p => !p)
  }

  const {
    mutate: deleteMutate,
    data: deleteResponse,
    reset,
    isLoading,
  } = useMutation(async (id: number) => await axiosDelete(token, refreshToken, `prizes/groups/${id}`), {
    onSuccess: (_, id) => {
      changeMessage(t('common.success'), 'success', reset)
      void queryClient.refetchQueries([queryKey])
    },
  })

  useEffect(() => {
    if (deleteResponse !== undefined && 'error' in deleteResponse) {
      changeMessage(deleteResponse?.error?.data?.message, 'error', reset)
    }
  }, [deleteResponse])

  return (
    <>
      <TableRow>
        {renderCells([
          {
            label: 'ID',
            value: (
              <Box display="flex" alignItems="center" gap={0.25}>
                <IconButton onClick={toggleShow} size="small">
                  {show ? <KeyboardArrowUpOutlinedIcon /> : <KeyboardArrowDownOutlinedIcon />}
                </IconButton>
                <LabelBox first={`#${row.id}`} />
              </Box>
            ),
          },
          {
            label: t('common.name'),
            value: (
              <LabelBox first={row.name} second={(row.translations || []).map(item => item.translation).join(', ')} />
            ),
          },
          {
            label: t('common.embargoDate'),
            value: row.embargoDate ? <LabelBox first={renderDate(row.embargoDate)} /> : '-',
          },
          {
            label: t('common.market'),
            value: <LabelBox first={row.market.name} second={row.market.description} />,
          },
          {
            label: t('common.availability'),
            value: (
              <Chip
                size="small"
                variant="outlined"
                color={percent(row.amountLeft, row.totalAmount).color}
                label={percent(row.amountLeft, row.totalAmount).percent}
              />
            ),
          },
          { label: t('common.type'), value: <LabelBox second={t(`prizes.types.${row.type}`)} /> },
          {
            label: t('common.marketName'),
            value: <LabelBox first={row.market?.name} second={row.market?.description} />,
          },
          {
            label: t('common.createdAt'),
            value: <LabelBox second={format(new Date(row.createdAt), 'MMM dd, yyyy')} />,
          },
        ])}
        {(ability.can('update', 'Prizes') || ability.can('delete', 'Prizes')) && (
          <TableCell>
            <Box display="flex" alignItems="center">
              {ability.can('delete', 'Prizes') && (
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => {
                      deleteMutate(row.id)
                    }}
                  >
                    <TrashIcon />
                  </IconButton>
                </Tooltip>
              )}
              {ability.can('update', 'Prizes') && (
                <Tooltip title="Edit">
                  <IconButton LinkComponent={Link} href={`/prizes/edit/${row.id}`}>
                    <PenIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </TableCell>
        )}
      </TableRow>
      {show && (
        <TableRow>
          <TableCell>&nbsp;</TableCell>
          <TableCell colSpan={5}>
            <Paper sx={{ p: 2, display: 'flex' }}>
              <Box sx={{ borderRight: '1px solid', borderColor: 'grey.200', flex: 1, pl: 3 }}>
                <Typography variant="body2" color="grey.500">
                  Total amount
                </Typography>
                <Typography variant="h6" component="p" color="grey.800">
                  {row.totalAmount}
                </Typography>
              </Box>
              <Box sx={{ borderRight: '1px solid', borderColor: 'grey.200', flex: 1, pl: 3 }}>
                <Typography variant="body2" color="grey.500">
                  Reserved
                </Typography>
                <Typography variant="h6" component="p" color="grey.800">
                  {row.statuses.find(item => item.status === 'reserved')?.count || 0}
                </Typography>
              </Box>
              <Box sx={{ borderRight: '1px solid', borderColor: 'grey.200', flex: 1, pl: 3 }}>
                <Typography variant="body2" color="grey.500">
                  Sent
                </Typography>
                <Typography variant="h6" component="p" color="grey.800">
                  {row.statuses.find(item => item.status === 'emitted')?.count || 0}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, pl: 3 }}>
                <Typography variant="body2" color="grey.500">
                  Available
                </Typography>
                <Typography variant="h6" component="p" color="grey.800">
                  {row.statuses.find(item => item.status === 'free')?.count || 0}
                </Typography>
              </Box>
            </Paper>
          </TableCell>
          <TableCell>&nbsp;</TableCell>
        </TableRow>
      )}
      {isLoading && <PageLoader />}
    </>
  )
}
